import React, { memo, useEffect, useState } from "react";
import { useLocation, Outlet } from "react-router-dom";
import { useGlobalSlice } from "app/slice";
import { useAppViewMode } from "app/hooks/use-app-view-mode";
import { PurchaseFlowNavigationGuard } from "../purchase-flow-navigation-guard";
import { externalUrls } from "utils/constants";
import { Header } from "./Header";
import {
  isHeaderBannerDisabled,
  isMaintenanceBannerMode,
  isPromoBannerMode,
} from "./Header/banner-config";
import { exactPaths, isHeaderRelativeForPath } from "./layout-paths";

export const AdminLayout = memo(() => {
  const location = useLocation();
  const isAppView = useAppViewMode();
  const [showAppDownload, setShowAppDownload] = useState(false);
  const [showPromoBanner, setShowPromoBanner] = useState(false);
  const [showMaintenanceBanner, setShowMaintenanceBanner] = useState(false);

  useEffect(() => {
    if (isMaintenanceBannerMode) {
      setShowMaintenanceBanner(true);
    }
  }, []);

  useEffect(() => {
    if (isPromoBannerMode) {
      setShowPromoBanner(true);
    }
  }, []);

  const shouldApplyMarginTop = !exactPaths.includes(location.pathname);

  const isHeaderRelative = isHeaderRelativeForPath(location.pathname);
  const isAgentPage = location.pathname.includes("agent");

  const shouldFetchPromoProduct =
    !isHeaderBannerDisabled &&
    isPromoBannerMode &&
    shouldApplyMarginTop &&
    !isAgentPage &&
    !Boolean(isAppView);

  const { useGetFreeProductVariantQuery } = useGlobalSlice();
  const { data: freeProductData, isSuccess } = useGetFreeProductVariantQuery(
    undefined,
    {
      skip: !shouldFetchPromoProduct,
    },
  );
  const promoProductId = freeProductData?.data?.product?._id;
  const hasPromoProduct = !!promoProductId;

  // Promo banner is visible only if the user hasn't dismissed it AND we have a product to show AND API succeeded
  const isPromoBannerVisible =
    isPromoBannerMode &&
    showPromoBanner &&
    hasPromoProduct &&
    isSuccess &&
    isAppView !== "minimal";

  const isMaintenanceBannerVisible =
    isMaintenanceBannerMode && showMaintenanceBanner && isAppView !== "minimal";

  useEffect(() => {
    const checkBannerVisibility = () => {
      // Check if user permanently dismissed the banner
      const permanentDismiss = localStorage.getItem(
        "vleAppBannerPermanentDismiss",
      );
      if (permanentDismiss === "true") {
        return false;
      }

      // Check if user dismissed temporarily (within last 7 days)
      const lastDismissed = localStorage.getItem("vleAppBannerDismissed");
      const now = Date.now();

      if (
        lastDismissed &&
        now - parseInt(lastDismissed) < 7 * 24 * 60 * 60 * 1000
      ) {
        return false;
      }

      // Check if it's the first visit today or haven't seen it today
      const lastSeenDate = localStorage.getItem("vleAppBannerLastSeen");
      if (
        !lastSeenDate ||
        new Date(parseInt(lastSeenDate)).toDateString() !==
          new Date().toDateString()
      ) {
        return true;
      }

      return false;
    };

    // Small delay for better UX (let page load first)
    const timer = setTimeout(() => {
      setShowAppDownload(checkBannerVisibility());
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = (permanent = false) => {
    setShowAppDownload(false);

    if (permanent) {
      localStorage.setItem("vleAppBannerPermanentDismiss", "true");
    } else {
      localStorage.setItem("vleAppBannerDismissed", Date.now().toString());
    }

    // Always update last seen date
    localStorage.setItem("vleAppBannerLastSeen", Date.now().toString());
  };

  const handleOpenApp = () => {
    // Track that user clicked the download button
    localStorage.setItem("vleAppBannerLastSeen", Date.now().toString());
    if (!externalUrls.agentResources) return;
    window.open(externalUrls.agentResources, "_blank", "noopener,noreferrer");
  };

  // const shouldShowIntercom =
  //   location.pathname.includes("network-plan") ||
  //   location.pathname.includes("selection") ||
  //   location.pathname.includes("phone-type") ||
  //   location.pathname.includes("number-type") ||
  //   location.pathname.includes("number-information");

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [location.pathname]);

  // Update last visit timestamp on component mount
  useEffect(() => {
    const now = Date.now();
    localStorage.setItem("lastVisit", now.toString());
  }, []);

  const contentMarginClass = (() => {
    if (isAppView === "hidden") return "mt-0 sm:mt-4";
    if (isAppView === "minimal" && !isHeaderRelative) {
      return "mt-16 sm:mt-20 lg:mt-24";
    }
    if (isHeaderRelative) return "mt-0";
    if (isAgentPage) return "mt-14 sm:mt-[4.375rem] lg:mt-[5.3125rem]";
    if (!shouldApplyMarginTop) return "mt-16 sm:mt-20 lg:mt-24";

    // mt-36
    const appDownloadMargin = showAppDownload ? "mt-24" : "mt-24";

    if (isMaintenanceBannerVisible) {
      return `${appDownloadMargin} mt-[8.5rem] sm:mt-[10.25rem] lg:mt-[12.125rem]`;
    }
    if (isPromoBannerVisible) {
      return `${appDownloadMargin} mt-32 sm:mt-[9.625rem] lg:mt-[11.375rem]`;
    }
    return `${appDownloadMargin} mt-24 sm:mt-[7.5rem] lg:mt-[9.125rem]`;
  })();

  const isMyAccountPage = location.pathname.startsWith("/my-account");
  const isCheckoutPage = location.pathname.startsWith("/checkout");

  const isOverflowClipLayoutPage = isMyAccountPage || isCheckoutPage;

  return (
    <React.Fragment>
      <PurchaseFlowNavigationGuard />
      {isAppView !== "hidden" && (
        <div
          className={
            isHeaderRelative
              ? "relative z-10 w-full bg-white"
              : "fixed top-0 left-0 z-10 w-full bg-white"
          }
        >
          <Header
            isHeaderRelative={isHeaderRelative}
            showAppDownload={showAppDownload}
            handleDismiss={handleDismiss}
            handleOpenApp={handleOpenApp}
            showPromoBanner={isPromoBannerVisible}
            setShowPromoBanner={setShowPromoBanner}
            promoProductId={promoProductId}
            showMaintenanceBanner={isMaintenanceBannerVisible}
            setShowMaintenanceBanner={setShowMaintenanceBanner}
          />
        </div>
      )}
      <div
        className={`flex overflow-hidden ${isOverflowClipLayoutPage ? "lg:overflow-clip" : ""} relative flex-col w-full ${
          isHeaderRelative ? "" : "min-h-dvh sm:min-h-screen"
        } ${isAgentPage ? "bg-[#F9FAFB]" : "bg-white"}`}
      >
        <div
          className={`w-full ${isOverflowClipLayoutPage ? "max-lg:overflow-y-auto" : "overflow-y-auto"} flex-1 ${contentMarginClass} transition-all duration-300 ease-in-out`}
        >
          <Outlet />
        </div>
        {/* {shouldShowIntercom && (
          <div className="fixed right-3 sm:right-7 bottom-10 sm:bottom-14 z-50 hidden sm:flex justify-center items-center bg-[#FE4D01] w-[3rem] h-[3rem] sm:w-[3.5rem] sm:h-[3.5rem] lg:w-[4rem] lg:h-[4rem] p-3 sm:p-4 rounded-full cursor-pointer">
            <img
              src="/assets/intercom.svg"
              className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8"
              alt="Intercom"
            />
          </div>
        )} */}
      </div>
    </React.Fragment>
  );
});
